import { useQuery, useQueryClient } from 'react-query';

import { deleteRequest, getRequest, postRequest, putRequest } from 'services/apiRequests';
import type { APIList, StringifiedUUID } from 'types';
import type { PlanningCycleWorkflow } from 'types/SPM';
import type { APIError } from 'utils/errors';

const baseUrl = (planningCycleId: StringifiedUUID) => `/api/v1/spm/planning-cycles/${planningCycleId}`;

type RunApprovalWorkflowPayload = {
  workflow_id: StringifiedUUID;
};

type CancelApprovalWorkflowPayload = {
  workflow_id: StringifiedUUID;
  workflow_instance_id: StringifiedUUID;
};

const listPlanningCycleWorkflows = (planningCycleId: StringifiedUUID) =>
  getRequest<null, APIList<PlanningCycleWorkflow>>(`${baseUrl(planningCycleId)}/list-workflows/`);

const runApprovalWorkflow = (
  planningCycleId: StringifiedUUID,
  payload: RunApprovalWorkflowPayload,
  csrfToken: string,
) =>
  postRequest<RunApprovalWorkflowPayload, void>(
    `${baseUrl(planningCycleId)}/run-approval-workflow/`,
    payload,
    csrfToken,
  );

const cancelApprovalWorkflow = (
  planningCycleId: StringifiedUUID,
  payload: CancelApprovalWorkflowPayload,
  csrfToken: string,
) =>
  postRequest<CancelApprovalWorkflowPayload, void>(
    `${baseUrl(planningCycleId)}/cancel-approval-workflow/`,
    payload,
    csrfToken,
  );

const runWorkflow = (planningCycleId: StringifiedUUID, payload: RunApprovalWorkflowPayload, csrfToken: string) =>
  postRequest<RunApprovalWorkflowPayload, void>(`${baseUrl(planningCycleId)}/run-workflow/`, payload, csrfToken);

const renameWorkflow = (
  planningCycleId: StringifiedUUID,
  workflowId: StringifiedUUID,
  workflowName: string,
  csrfToken: string,
) =>
  putRequest<{ workflow_name: string }, null>(
    `${baseUrl(planningCycleId)}/update-workflows/${workflowId}/`,
    { workflow_name: workflowName },
    csrfToken,
  );

const deleteWorkflow = (planningCycleId: StringifiedUUID, workflowId: StringifiedUUID, csrfToken: string) =>
  deleteRequest<null, null>(`${baseUrl(planningCycleId)}/delete-workflows/${workflowId}/`, null, csrfToken);

// Query keys
const spmWorkflowsKeys = {
  base: () => ['planning-cycles'] as const,
  planningCycle: (planningCycleId: StringifiedUUID) => [...spmWorkflowsKeys.base(), planningCycleId] as const,
  workflows: (planningCycleId: StringifiedUUID) =>
    [...spmWorkflowsKeys.planningCycle(planningCycleId), 'workflows'] as const,
};

// React Query hooks
const useListPlanningCycleWorkflowsQuery = (planningCycleId: StringifiedUUID) => {
  const QUERY_KEY = spmWorkflowsKeys.workflows(planningCycleId);
  const queryClient = useQueryClient();

  const query = useQuery<APIList<PlanningCycleWorkflow>, APIError>(QUERY_KEY, () =>
    listPlanningCycleWorkflows(planningCycleId),
  );

  const invalidateQuery = () => queryClient.invalidateQueries(QUERY_KEY);

  return { query, invalidateQuery };
};

export const SPMWorkflowService = {
  spmWorkflowsKeys,
  useListPlanningCycleWorkflowsQuery,
  cancelApprovalWorkflow,
  runApprovalWorkflow,
  runWorkflow,
  renameWorkflow,
  deleteWorkflow,
};
